import * as React from 'react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as analytics from '@opendoor/shared-fe/build/analytics';
import { Box as NoxoBox } from '@opendoor/bricks-next';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { Box, Button, Heading, Loader, Text } from '@opendoor/bricks/core';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { NamelessAccountContextProvider } from '@opendoor/cinderblocks/growth/auth/NamelessAccountContext';
import type { DisclaimerProps } from '@opendoor/cinderblocks/platform/auth/shared';

import NavigationControl from 'src/components/sellerFlow/NavigationControl';
import { buildAccountCreationAnswers } from 'src/helpers/answers';
import { BASE_URL } from 'src/helpers/globals';
import { Session } from 'src/helpers/session';
import { usePromptGoogleOneTap } from 'src/helpers/usePromptGoogleOneTap';
import { isOpendoorAppEvoWebView } from 'src/helpers/webview';
import { useGetHumanQuery, useLazyValidateEmailQuery } from 'src/store/api';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getCurrentSlug } from 'src/store/router/selectors';
import {
  handleException,
  removeHuman,
  sendPasswordSetUpEmail as sendPasswordSetUpEmailAction,
} from 'src/store/sellerFlow/actions';
import { AnswerSet } from 'src/store/sellerFlow/AnswerSet';
import {
  answersIsSubmitting as getAnswersIsSubmitting,
  isInExclusiveOffersTreatment as getIsInExclusiveOffersTreatment,
  leadToken as getLeadToken,
  getPreviousPath,
  progressPercentage as getProgressPercentage,
  submittedByAgent as getSubmittedByAgent,
  isPhoneNumberRequired,
} from 'src/store/sellerFlow/selectors';
import { changeAnswers } from 'src/store/sellerInput';
import {
  submitAndGoToNextPage as uiSubmitAndGoToNextPage,
  submitAnswers as uiSubmitAnswers,
} from 'src/store/ui';

import Layout from '../flowPage/Layout';

const AccountForm = lazy(() => import('@opendoor/cinderblocks/platform/auth/AccountForm'));

const AccountPage: React.FC = () => {
  const [validateEmail] = useLazyValidateEmailQuery();
  const [hasGeneratedPassword, setHasGeneratedPassword] = React.useState(false);
  const [isNewHuman, setIsNewHuman] = React.useState(true);
  const [emailLoading, setEmailLoading] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [passwordLoading, setPasswordLoading] = React.useState(false);
  const [signOutLoading, setSignOutLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  const [emailRegistrationError, setEmailRegistrationError] = React.useState<string | undefined>();
  const [showError, setshowError] = React.useState(false);
  const answersIsSubmitting = useAppSelector(getAnswersIsSubmitting);

  const currentSlug = useAppSelector(getCurrentSlug);
  const progressPercentage = useAppSelector(getProgressPercentage);
  const previousPath = useAppSelector(getPreviousPath);
  const sellerInputUuid = useAppSelector(getLeadToken);
  const inExclusiveOfferTreatment = useAppSelector(getIsInExclusiveOffersTreatment);

  const { data: human, refetch: refetchHuman } = useGetHumanQuery();

  const accountCreationTitle = useAppSelector((state) => state.ui.page?.title) as string;
  const accountCreationSubtitle = 'Sign in or enter your email address.';

  const phoneNumberRequired = useAppSelector(isPhoneNumberRequired);

  const submittedByAgent = useAppSelector(getSubmittedByAgent);
  const registrationHeaderText = `You're all set! ${
    submittedByAgent ? "Your client's" : 'Your'
  } offer is ready to go.`;
  const registrationSubHeaderText = `Add your info so we know who ${
    submittedByAgent ? 'you are.' : "it's for."
  }`;

  const standardTcpaDisclaimerText = ({ actionText }: { actionText: string }) =>
    `By clicking "${actionText}" I agree that Opendoor may call or text me about my inquiry, including by automated means. I may opt out at any time, and consent is not a condition of using Opendoor's services.`;

  const [needsToLogin, setNeedsToLogin] = useState(false);
  const [hidePasswordField, setHidePasswordField] = useState(true);

  useEffect(() => {
    if (hidePasswordField && needsToLogin) {
      // Shouldn't hide password field for existing user who needs to login
      setHidePasswordField(false);
    }
    // eslint-disable-next-line
  }, [needsToLogin]);

  useEffect(() => {
    if (emailRegistrationError) {
      showToast(
        'error',
        'Unable to register your account. Please try again.',
        'reception-fe-nameless-register-error-toast',
        {
          toastOptions: { autoClose: 7000 },
        },
      );
    }
  }, [emailRegistrationError]);

  usePromptGoogleOneTap((response) => {
    onRegister(
      undefined, // email
      undefined, // name
      undefined, // password
      undefined, // phone
      response.credential, // googleAuthId
    );
  });

  const dispatch = useAppDispatch();

  const submitChangedAnswers = (answers, shouldGoToNextPage) => {
    analytics.trackEvent('flow-slug', 'change', 'account-submit-changed-answers', {
      answers,
      shouldGoToNextPage,
    });
    const keys = Object.keys(answers) ?? [];
    const newAnswers = keys.map((key) => ({ key, answer: answers[key] }));
    dispatch(changeAnswers(newAnswers));
    if (shouldGoToNextPage) {
      dispatch(uiSubmitAndGoToNextPage({ additionalAnswerKeysToSubmit: keys }));
    } else {
      dispatch(uiSubmitAnswers({ additionalAnswerKeysToSubmit: keys }));
    }
  };

  const sendPasswordSetUpEmail = (email: string) => {
    const redirectUrl = `${BASE_URL}/seller-lead/${sellerInputUuid}/${currentSlug}`;
    dispatch(sendPasswordSetUpEmailAction(email, redirectUrl));
  };

  const handleSubmit = (event: any) => {
    event.stopPropagation();
  };

  const onLogin = (email: string, password: string) => {
    analytics.trackEvent('flow-slug', 'change', 'account-on-login', {
      currentSlug,
      seller_input_uuid: sellerInputUuid,
    });

    setPasswordLoading(true);
    Session.logIn(email, password)
      .then((data) => {
        return Promise.resolve(updateSellerDetails(data.full_name, data.email, data.primary_phone));
      })
      .catch((err) => {
        dispatch(handleException(err, 'sign-in', 'warn'));
        setError(err.toString());
        setPasswordLoading(false);
      });
  };

  const onRequestResetPassword = (email: string) => {
    Session.requestResetPassword(email);
  };

  const updateSellerEmail = (email: string) => {
    submitChangedAnswers(
      {
        'seller.email': email,
      },
      false,
    );
  };

  const updateSellerDetails = (
    name: string,
    email: string,
    phone?: string,
    extraAnswers?: AnswerSet,
  ) => {
    const answersToSubmit = buildAccountCreationAnswers(name, email, phone);
    if (inExclusiveOfferTreatment && phone) {
      // We don't want to save their number to the seller.phone_number field
      // Since the disclaimer doesn't allow for marketing use
      answersToSubmit['seller.exclusives_customer_phone_number'] = phone;
      if (answersToSubmit['seller.phone_number']) {
        delete answersToSubmit['seller.phone_number'];
      }
    }
    const mergedAnswers = { ...answersToSubmit, ...extraAnswers };
    submitChangedAnswers(mergedAnswers, true);
    setPasswordLoading(false);
  };

  const onAccountUpdate = (name: string, phone?: string) => {
    try {
      setPasswordLoading(true);
      updateSellerDetails(name, email, phone, undefined);
    } catch (err) {
      dispatch(handleException(err, 'update-seller-details', 'warn'));
      setError(err.toString());
    } finally {
      setPasswordLoading(false);
    }
  };

  const onRegister = (
    email?: string,
    name?: string,
    // we no longer capture a password here, but this parameter is still passed
    // in on calls to onRegister
    _password?: string,
    phone?: string,
    googleAuthId?: string,
    facebookAuthId?: string,
    passwordlessToken?: string,
    hasGeneratedPassword?: boolean,
  ) => {
    if (googleAuthId || facebookAuthId) {
      setEmailLoading(true);
    } else {
      setPasswordLoading(true);
    }

    hasGeneratedPassword = true;

    if (inExclusiveOfferTreatment) {
      // We are requiring phone numbers for Exclusives and based on disclaimer
      // cannot use their number for marketing purposes and hence this can't be stored on the human
      Session.register(
        email,
        name,
        undefined,
        undefined,
        googleAuthId,
        facebookAuthId,
        passwordlessToken,
        hasGeneratedPassword,
      )
        .then((data) => {
          return Promise.resolve(updateSellerDetails(data.full_name, data.email, phone));
        })
        .catch((err) => {
          dispatch(handleException(err, 'register', 'warn'));
          const error = err.toString();
          setError(error);
          if (error === 'Error: 422 ') {
            // user already exists with email, prompt to log in
            if (email) {
              updateSellerEmail(email);
              setHidePasswordField(false);
              setEmail(email);
            }
            setIsNewHuman(false);
          }
          setPasswordLoading(false);
          setEmailLoading(false);
        });
    } else {
      Session.register(
        email,
        name,
        undefined,
        phone,
        googleAuthId,
        facebookAuthId,
        passwordlessToken,
        hasGeneratedPassword,
      )
        .then((data) => {
          return Promise.resolve(
            updateSellerDetails(data.full_name, data.email, data.primary_phone, undefined),
          );
        })
        .catch((err) => {
          dispatch(handleException(err, 'register', 'warn'));
          const error = err.toString();
          setError(error);
          if (error === 'Error: 422 ') {
            // user already exists with email, prompt to log in
            if (email) {
              updateSellerEmail(email);
              setHidePasswordField(false);
              setEmail(email);
            }
            setIsNewHuman(false);
          }
          setPasswordLoading(false);
          setEmailLoading(false);
        });
    }
  };

  const onNamelessHumanStatusRequest = (email: string) => {
    setEmailLoading(true);
    Session.requestHumanStatus(email)
      .then((data) => {
        // Human already exists with email, show login
        setNeedsToLogin(true);
        setIsNewHuman(false);
        const { has_generated_password } = data;
        setHasGeneratedPassword(!!has_generated_password);
        setEmailLoading(false);
        if (has_generated_password) {
          sendPasswordSetUpEmail(email);
        }
      })
      .catch((exception) => {
        // No human exists with email, show account sign up
        dispatch(handleException(exception, 'human-status-request', 'warn'));
        setIsNewHuman(true);
        setNeedsToLogin(false);
        setEmailRegistrationError('');

        Session.register(
          email,
          'Customer',
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          true,
        )
          .then(() => {
            setEmail(email);
            return Promise.resolve(
              submitChangedAnswers(buildAccountCreationAnswers('Customer', email), false),
            );
          })
          .catch(() => {
            setEmailRegistrationError('Registration failed. Please try again.');
            setEmailLoading(false);
          });
        setEmailLoading(false);
      });
  };

  const renderBackButton = () => {
    return previousPath && !inExclusiveOfferTreatment ? (
      <RouterLink style={{ color: 'var(--colors-brand50)' }} to={previousPath}>
        &lt; Back
      </RouterLink>
    ) : null;
  };

  const renderNavigationControl = () => {
    return (
      <Box width="100%" maxWidth={['400px', null, 'initial']}>
        <NavigationControl
          previousPath={previousPath}
          hideNextButton={true}
          isStickyOnMobile={false}
        />
      </Box>
    );
  };

  const onContinue = () => {
    analytics.trackEvent('flow-slug', 'change', 'account-on-continue', {
      currentSlug,
      human,
      seller_input_uuid: sellerInputUuid,
    });

    setPasswordLoading(true); // mimic signing in
    updateSellerDetails(human?.full_name ?? '', human?.email ?? '', human?.primary_phone ?? '');
  };

  const onSignOut = () => {
    setSignOutLoading(true);
    Session.logOut()
      .then((_) => {
        setSignOutLoading(false);
        refetchHuman();
      })
      .catch((exception) => {
        dispatch(handleException(exception, 'sign-out', 'warn'));
        setSignOutLoading(false);
        dispatch(removeHuman());
        setshowError(true);
      });
  };

  const onValidateEmail = async (
    email: string,
  ): Promise<{ suggestion: string; valid: boolean }> => {
    if (email.indexOf('@opendoorqa.com') > 0) {
      // Don't run validation on smoke test leads, don't waste money
      return Promise.resolve({ suggestion: '', valid: true });
    }

    return validateEmail(email).then((res) => {
      return res.data ? res.data : { suggestion: '', valid: true };
    });
  };

  const phoneDisclaimerProps: DisclaimerProps = {
    // has to be on top of the actual button
    location: 'top',
    formatText: standardTcpaDisclaimerText,
  };

  const showNovoStyle = useAppSelector((state) => state.featureFlags.novoAuthStyle);

  return (
    <ThemedLite theme={inExclusiveOfferTreatment ? 'exclusives' : 'legacy'}>
      <Layout
        onSubmit={handleSubmit}
        progressPercentage={progressPercentage || 0}
        renderBackButton={renderBackButton}
        hideProgressBar={inExclusiveOfferTreatment}
        renderNavigationControl={isOpendoorAppEvoWebView ? undefined : renderNavigationControl}
        // TODO: make sure...
        currentSlug="account"
        hideFooter={isOpendoorAppEvoWebView}
      >
        {human && human.email ? (
          <AccountConfirmation
            email={human.email}
            onContinue={onContinue}
            onSignOut={onSignOut}
            title={accountCreationTitle}
            subtitle={isOpendoorAppEvoWebView ? undefined : accountCreationSubtitle}
            continueLoading={passwordLoading}
            signOutLoading={signOutLoading}
            error={showError}
          />
        ) : (
          <NoxoBox
            width="100%"
            pt={5}
            py="$24x"
            px="$12x"
            mx="auto"
            justifyContent="center"
            alignItems="center"
            $largerThanMD={{ py: '$36x', px: 0 }}
          >
            <Suspense fallback={<Loader />}>
              <NamelessAccountContextProvider
                shouldRegisterOnEmailSubmit={true}
                onAccountUpdate={onAccountUpdate}
                emailRegistrationError={emailRegistrationError}
              >
                <AccountForm
                  showNovoStyle={showNovoStyle}
                  analyticsName="reception-fe-account-form"
                  loginHeaderText={accountCreationTitle}
                  loginSubHeaderText={accountCreationSubtitle}
                  onHumanStatusRequest={onNamelessHumanStatusRequest}
                  onLogin={onLogin}
                  onRegister={onRegister}
                  email={email}
                  emailLoading={emailLoading}
                  passwordLoading={passwordLoading || answersIsSubmitting}
                  hasGeneratedPassword={hasGeneratedPassword}
                  isNewHuman={isNewHuman}
                  error={error}
                  showGoogleAuth
                  phoneNumberInputOptional={!phoneNumberRequired}
                  openForgotPasswordInNewTab
                  customEmailValidityCheck={onValidateEmail}
                  customEmailValidityCheckDelay={500}
                  requestResetPassword={onRequestResetPassword}
                  containerProps={{ maxWidth: '100%', padding: 0 }}
                  hidePasswordField={hidePasswordField}
                  registrationHeaderText={registrationHeaderText}
                  registrationSubHeaderText={registrationSubHeaderText}
                  onboardingAccountPageCTA={true}
                  fullNamePlaceholder={submittedByAgent ? 'John Smith' : 'Jasper Smith'}
                  fullNameLabel={submittedByAgent ? 'Full name of agent' : 'Full name'}
                  disclaimerProps={phoneDisclaimerProps}
                  unsetContainer={true}
                />
              </NamelessAccountContextProvider>
            </Suspense>
          </NoxoBox>
        )}
      </Layout>
    </ThemedLite>
  );
};

interface IAccountConfirmationProps {
  email: string;
  onContinue: () => void;
  onSignOut: () => void;
  title: string;
  subtitle?: string;
  continueLoading?: boolean;
  signOutLoading?: boolean;
  error?: boolean;
}

const AccountConfirmation: React.FC<IAccountConfirmationProps> = ({
  email,
  onContinue,
  onSignOut,
  title,
  subtitle,
  continueLoading,
  signOutLoading,
  error,
}) => {
  useEffect(() => {
    if (error) {
      showToast(
        'error',
        'Unable to sign out. Please try again.',
        'reception-fe-sign-out-error-toast',
        {
          toastOptions: { autoClose: 7000 },
        },
      );
    }
  }, [error]);

  return (
    <Box mb={[6, 7]} width="100%" textAlign={['unset', 'unset', 'center']} pt={[0, 7]}>
      <Heading size="UNSAFE_h2" as="h2" mb={7} fontSize={['s3', 'UNSAFE_h2']}>
        {title}
      </Heading>
      {/* eslint-disable-next-line react/jsx-pascal-case */}
      {subtitle && (
        <Text fontSize="s1" lineHeight="s0" mb={5}>
          {subtitle}
        </Text>
      )}
      {/* eslint-disable-next-line react/jsx-pascal-case */}
      <Text fontSize="s1" lineHeight="s0" mb={10}>
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        You are signed in as{' '}
        <Text fontWeight="bold" color="neutrals100" as="span">
          {email}
        </Text>
      </Text>
      <Box width="100%" mb={5}>
        <Button
          onClick={onContinue}
          loading={continueLoading}
          analyticsName="reception-fe-continue-signed-in"
          aria-label="Continue"
        >
          Continue
        </Button>
      </Box>
      {!isOpendoorAppEvoWebView && (
        <Button
          variant="link"
          onClick={onSignOut}
          loading={signOutLoading}
          analyticsName="reception-fe-sign-out-switch-account"
          aria-label="Sign out and switch to a different account"
        >
          Sign out and switch to a different account
        </Button>
      )}
    </Box>
  );
};

// For passwordless account creation experiment
// TODO: if we roll out to 100%, move to helpers file
export const generatePassword = (passwordLength: number): string => {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let password = '';
  for (let i = 0; i <= passwordLength; i++) {
    const randomNumber = Math.floor(Math.random() * chars.length);
    password += chars.substring(randomNumber, randomNumber + 1);
  }
  return password;
};

export default AccountPage;
